import { useMemo } from 'react';

import { useAuth } from '@dotfile/frontend/shared/common';
import {
  Heading,
  HStack,
  Spacer,
  useIsMobile,
  useIsSmallScreen,
} from '@dotfile/frontend/shared/design-system';
import { ContactStateEnum } from '@dotfile/shared/domain';

import { useRouter } from '../../../routes';
import { ROOT_ROUTES } from '../../../routes/root-routes.constant';
import { CaseMenu, LanguageSelector } from '../../components';
import { ContactAuthContext } from '../../contexts/auth/type';
import {
  useContactCases,
  useLatestClientPortalVersion,
  useReadableColor,
} from '../../hooks';
import { Logo } from './logo';
import { LogoutButton } from './logout-button';

export const Header = () => {
  const isMobile = useIsMobile();
  const { data } = useLatestClientPortalVersion();
  const { auth } = useAuth<ContactAuthContext>();
  const { data: contactCasesData, loading: loadingContactCases } =
    useContactCases();
  const readableColor = useReadableColor();
  const isSmallScreen = useIsSmallScreen();
  const router = useRouter();
  const route = router.useRoute(ROOT_ROUTES);
  const currentRouteIsNotOnboarding = route?.name !== 'Onboardings';
  const { logo } = data?.latestClientPortalVersion.setting?.styling ?? {};

  const isNewState = auth.state === ContactStateEnum.new;
  const { currentCase, hasMultipleCases } = useMemo(() => {
    const hasMultipleCases = (contactCasesData?.contactCases ?? []).length > 1;
    const currentCase = hasMultipleCases
      ? contactCasesData?.contactCases.find((c) => c.id === auth.caseId)
      : undefined;

    return { currentCase, hasMultipleCases };
  }, [contactCasesData, auth.caseId]);

  if (!data) return null;

  return (
    <HStack
      height="16"
      py="3"
      px={isSmallScreen ? '6' : '12'}
      width="full"
      backgroundColor="custom.500"
    >
      <HStack height="full" spacing="8">
        {logo?.publicUrl ? (
          <Logo url={logo.publicUrl} name={logo?.name} />
        ) : (
          <Heading
            as="h1"
            fontSize={isMobile ? 'lg' : 'xl'}
            noOfLines={1}
            color={readableColor}
          >
            {data.latestClientPortalVersion.clientPortal.name}
          </Heading>
        )}

        {hasMultipleCases &&
          currentCase &&
          !isMobile &&
          currentRouteIsNotOnboarding && (
            <Heading
              as="h2"
              fontSize={isMobile ? 'md' : 'lg'}
              noOfLines={1}
              color={readableColor}
            >
              {currentCase.name}
            </Heading>
          )}
      </HStack>

      <Spacer />

      {(hasMultipleCases || isNewState) && currentRouteIsNotOnboarding ? (
        <CaseMenu />
      ) : !loadingContactCases ? (
        <LogoutButton />
      ) : null}

      {!isSmallScreen && <LanguageSelector color={readableColor} />}
    </HStack>
  );
};
